import { supabase } from '../supabase';

export class BiohackRatingService {
  // Fetch average ratings for a biohack
  static async getAverageRatings(hackId) {
    try {
      const { data, error } = await supabase
        .from('hack_scoring')
        .select('ease_of_use_score, affordability_score, time_efficiency_score')
        .eq('hack_id', hackId);

      if (error) throw error;

      if (!data || data.length === 0) {
        return {
          ease_of_use_score: 0,
          affordability_score: 0,
          time_efficiency_score: 0,
          total_ratings: 0
        };
      }

      // Calculate averages
      const totals = data.reduce((acc, rating) => {
        acc.ease_of_use_score += rating.ease_of_use_score || 0;
        acc.affordability_score += rating.affordability_score || 0;
        acc.time_efficiency_score += rating.time_efficiency_score || 0;
        return acc;
      }, {
        ease_of_use_score: 0,
        affordability_score: 0,
        time_efficiency_score: 0
      });

      const count = data.length;
      return {
        ease_of_use_score: Math.round((totals.ease_of_use_score / count) * 10) / 10,
        affordability_score: Math.round((totals.affordability_score / count) * 10) / 10,
        time_efficiency_score: Math.round((totals.time_efficiency_score / count) * 10) / 10,
        total_ratings: count
      };
    } catch (error) {
      console.error('Error fetching average ratings:', error);
      return null;
    }
  }

  // Fetch user's rating for a specific biohack
  static async getUserRating(userId, hackId) {
    try {
      // Check if user has rated this biohack (any rating entry)
      const { data, error } = await supabase
        .from('hack_scoring')
        .select('*')
        .eq('user_id', userId)
        .eq('hack_id', hackId)
        .limit(1); // Just get one record to check if they've rated

      if (error) {
        throw error;
      }

      // Return the first rating if it exists, null otherwise
      return data && data.length > 0 ? data[0] : null;
    } catch (error) {
      console.error('Error fetching user rating:', error);
      return null;
    }
  }

  // Check if user has rated a biohack (returns boolean)
  static async hasUserRated(userId, hackId) {
    try {
      const { count, error } = await supabase
        .from('hack_scoring')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('hack_id', hackId);

      if (error) {
        throw error;
      }

      return count > 0;
    } catch (error) {
      console.error('Error checking if user has rated:', error);
      return false;
    }
  }

  // Submit a new rating entry
  static async submitRating(ratingData) {
    try {
      console.log('BiohackRatingService.submitRating called with:', ratingData);
      
      // Always insert a new rating entry to keep history
      console.log('Inserting new rating entry');
      const { data, error } = await supabase
        .from('hack_scoring')
        .insert(ratingData)
        .select()
        .single();
      
      if (error) {
        console.error('Error inserting rating:', error);
        throw error;
      }
      
      console.log('Rating submission successful:', data);
      return data;
    } catch (error) {
      console.error('Error submitting rating:', error);
      throw error;
    }
  }

  // Get rating statistics for a biohack
  static async getRatingStats(hackId) {
    try {
      const { data, error } = await supabase
        .from('hack_scoring')
        .select('ease_of_use_score, affordability_score, time_efficiency_score')
        .eq('hack_id', hackId);

      if (error) throw error;

      if (!data || data.length === 0) {
        return {
          total_ratings: 0,
          average_rating: 0,
          rating_distribution: {}
        };
      }

      // Calculate overall average
      const totalScores = data.reduce((acc, rating) => {
        return acc + (rating.ease_of_use_score + rating.affordability_score + rating.time_efficiency_score) / 3;
      }, 0);

      const averageRating = Math.round((totalScores / data.length) * 10) / 10;

      // Calculate rating distribution
      const distribution = {};
      data.forEach(rating => {
        const avg = Math.round((rating.ease_of_use_score + rating.affordability_score + rating.time_efficiency_score) / 3);
        distribution[avg] = (distribution[avg] || 0) + 1;
      });

      return {
        total_ratings: data.length,
        average_rating: averageRating,
        rating_distribution: distribution
      };
    } catch (error) {
      console.error('Error fetching rating stats:', error);
      return null;
    }
  }

  // Get top rated biohacks
  static async getTopRatedBiohacks(limit = 10) {
    try {
      // This is a more complex query that would need to be implemented
      // For now, we'll return a simple structure
      const { data, error } = await supabase
        .from('hack_scoring')
        .select(`
          hack_id,
          biohacks!hack_scoring_hack_id_fkey(
            id,
            name,
            image_url,
            description
          )
        `)
        .limit(limit);

      if (error) throw error;

      // Group by hack_id and calculate averages
      const hackRatings = {};
      data.forEach(rating => {
        const hackId = rating.hack_id;
        if (!hackRatings[hackId]) {
          hackRatings[hackId] = {
            hack: rating.biohacks,
            ratings: [],
            average: 0
          };
        }
        hackRatings[hackId].ratings.push(rating);
      });

      // Calculate averages and sort
      const topRated = Object.values(hackRatings)
        .map(hackData => {
          const totalScore = hackData.ratings.reduce((acc, rating) => {
            return acc + (rating.ease_of_use_score + rating.affordability_score + rating.time_efficiency_score) / 3;
          }, 0);
          hackData.average = Math.round((totalScore / hackData.ratings.length) * 10) / 10;
          return hackData;
        })
        .sort((a, b) => b.average - a.average)
        .slice(0, limit);

      return topRated;
    } catch (error) {
      console.error('Error fetching top rated biohacks:', error);
      return [];
    }
  }
}
